import { type GuidanceSession } from '@fruitful/types';
import { useState } from 'react';
import {
    BodyLargeEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckIcon,
    ScrollView,
    Spinner,
    useToastController,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    RichTextEditor,
    unpackRichText,
} from '../../common/components/RichTextEditor';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { formatToSimpleDateTimeString } from '../../common/utils/formatting';
import type { MembershipDetails } from '../../entities/entityTypes';
import { useRefetchMembershipDetailsMutationOptions } from '../../entities/membership/hooks/useRefetchMembershipDetailsMutationOptions';
import { captureException } from '../../logger/Logger';
import { useUpdateGuidanceSession } from '../hooks/useUpdateGuidanceSession';
import { getMembershipGuidanceSessionDisplayName } from '../utils/membershipGuidanceSessionDisplayName';

interface GuidanceSessionNotesModalProps {
    isOpen: boolean;
    onClose: () => void;
    session: GuidanceSession;
    membership: MembershipDetails;
}

export const GuidanceSessionNotesModal = ({
    isOpen,
    onClose,
    session,
    membership,
}: GuidanceSessionNotesModalProps) => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const toast = useToastController();

    const [sessionSummary, setSessionSummary] = useState(
        unpackRichText(session.notes),
    );
    const mutationOptions =
        useRefetchMembershipDetailsMutationOptions(membership);

    const { mutateAsync: updateGuidanceSession } =
        useUpdateGuidanceSession(mutationOptions);

    const onSave = async () => {
        try {
            setIsSubmitting(true);
            await updateGuidanceSession({
                urlParams: { id: session.id },
                body: {
                    notes: JSON.stringify(sessionSummary),
                },
            });

            setIsSubmitting(false);
            onClose();
            toast.show('Session notes updated!', MutationSuccessToastOptions);
        } catch (e) {
            toast.show('Something went wrong!', MutationErrorToastOptions(e));
            captureException(e);
        }
    };

    return (
        <GroveSimpleModal isOpen={isOpen} onClose={onClose} hasCloseButton>
            <ScrollView>
                <YStack width={1200} margin={16} gap={16}>
                    <BodyLargeEmphasized>
                        {getMembershipGuidanceSessionDisplayName(
                            session.type,
                            membership,
                        )}{' '}
                        on {formatToSimpleDateTimeString(session.startTime)}
                    </BodyLargeEmphasized>
                    <RichTextEditor
                        placeholder="Session Summary"
                        initial={sessionSummary}
                        onChange={setSessionSummary}
                        // Approx. 24 lines
                        height={24 * 24}
                    />
                    {isSubmitting ? (
                        <XStack gap={16} alignItems="center">
                            <Spinner />
                        </XStack>
                    ) : (
                        <XStack gap={16} alignItems="center">
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.SMALL}
                                text="Cancel"
                                disabled={isSubmitting}
                                icon={XIcon}
                                buttonProps={{ flexShrink: 1 }}
                                onPress={() => {
                                    onClose();
                                }}
                            />
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.SMALL}
                                tone="positive"
                                text="Save"
                                disabled={isSubmitting}
                                icon={CheckIcon}
                                buttonProps={{ flexShrink: 1 }}
                                onPress={async () => {
                                    await onSave();
                                }}
                            />
                        </XStack>
                    )}
                </YStack>
            </ScrollView>
        </GroveSimpleModal>
    );
};
